// eslint-disable-next-line @nx/enforce-module-boundaries
import { useEventMixpanel } from '@mixpanel/hooks/use-event-mixpanel';
import { useQueryClient } from '@tanstack/react-query';

import { ProductAlertService } from '@core/api';
import { useContextUtil } from '@core/context';
import {
  EQueryKey,
  ProductAlertCreationProps,
  ProductAlertEntities,
  ProductAlertResponse,
  ProductAlertUpdateProps,
} from '@core/type/api';
import { HookStoreFn, ProductAlertHook, ProductAlertState } from '@core/type/context';
import { EMixpanelEventName } from '@core/type/interface/mixpanel';

import { useLanguage } from './use-language';
import { useMutationApi } from './use-mutation-api';
import { useParamsQueriesKeys, useQueryApi } from './use-query-api';

export const useProductAlert: HookStoreFn<
  ProductAlertState,
  ProductAlertHook
> = (): ProductAlertHook => {
  const { axiosInstance } = useContextUtil();
  const { language } = useLanguage();
  const { trackAlertEvent } = useEventMixpanel();

  const queryClient = useQueryClient();

  const queries = useParamsQueriesKeys(EQueryKey.PRODUCT_ALERTS);

  const { data: productAlertsData } = useQueryApi<ProductAlertResponse>(EQueryKey.PRODUCT_ALERTS);

  const { mutate: removeFromProductAlerts, isSuccess: isRemoveSucceed } = useMutationApi(
    async (productAlertId: number) =>
      ProductAlertService.deleteAlert(axiosInstance, language, productAlertId),
    {
      mutationKey: queries,
      onSuccess: (_, alertId: number) => {
        queryClient.setQueryData(queries, (data: ProductAlertResponse): ProductAlertResponse => {
          const alert = data.productAlerts.find((stockAlert) => stockAlert.id === alertId);
          trackAlertEvent(
            EMixpanelEventName.PRODUCT_ALERT,
            'delete',
            window.location.pathname,
            alert.notification,
            { alertValue: alert.totalPrice },
            alert.product,
          );
          return {
            productAlerts: data?.productAlerts?.filter((alert) => alert.id !== alertId) ?? [],
          };
        });
      },
    },
  );

  const addProductAlert = async ({ submitData, product }: ProductAlertCreationProps) =>
    ProductAlertService.addAlert(axiosInstance, language, submitData).then(
      ({ productAlert: { id } }) => {
        queryClient.setQueryData(queries, (data: ProductAlertResponse): ProductAlertResponse => {
          trackAlertEvent(
            EMixpanelEventName.PRODUCT_ALERT,
            'create',
            window.location.pathname,
            submitData.notification,
            { alertValue: submitData.amount },
            product,
          );
          return {
            productAlerts: data.productAlerts?.concat([
              {
                totalPrice: submitData.amount,
                currencyIso: submitData.currencyIso,
                notification: submitData.notification,
                id,
                product,
              },
            ]),
          };
        });
      },
    );

  const { mutate: updateProductAlert } = useMutationApi(
    async ({ id, amount }: ProductAlertUpdateProps) =>
      ProductAlertService.patchAlert(axiosInstance, language, id, { amount }),
    {
      onSuccess: (_, { id, amount }: ProductAlertUpdateProps) => {
        queryClient.setQueryData(queries, (data: ProductAlertResponse): ProductAlertResponse => {
          const alert = data.productAlerts.find((stockAlert) => stockAlert.id === id);
          trackAlertEvent(
            EMixpanelEventName.PRODUCT_ALERT,
            'update',
            window.location.pathname,
            alert.notification,
            { alertValue: amount },
            alert.product,
          );
          const productAlerts: ProductAlertEntities = data?.productAlerts?.map((productAlert) =>
            productAlert.id === id ? { ...productAlert, totalPrice: amount } : productAlert,
          );
          return { productAlerts };
        });
      },
    },
  );

  return {
    productAlerts: productAlertsData?.productAlerts ?? [],
    removeFromProductAlerts,
    isRemoveSucceed,
    addProductAlert,
    updateProductAlert,
  };
};
